import React, { useEffect, useState } from "react";
import { profileAPI, UserProfile } from "../services/profile";
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { AlertCircle, User as UserIcon, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";
import PageWithNavbar from "@/components/PageWithNavbar";

export const UserProfilePage =() => {
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isActive, setIsActive] = useState<boolean>(false);

  useEffect(() => {
    setLoading(true);
    setError(null);
    const fetchProfile = async () => {
        try {
            const profile = await profileAPI.getProfile();
            setProfile(profile);
        } 
        catch (err: any) {
            setError(err.message || "Failed to load profile.");
        } 
        finally 
        {
            setLoading(false);
        }
        console.log("Profile loaded:", profile);
    };
    fetchProfile();
  }, []);
  useEffect(() => {
    if (profile) {
        setIsActive(profile?.accountstatus == 20 || profile?.accountstatus == 50 || profile?.accountstatus == 100 || profile?.accountstatus == 200);
    }
  }, [profile]);

  const [editOpen, setEditOpen] = useState(false);

  const [showFullUsername, setShowFullUsername] = useState(false);

  // Helper to cap username
  const getDisplayUsername = (username: string) => {
    if (showFullUsername || username.length <= 26) return username;
    return username.slice(0, 26) + '...';
  };

  const [showFullEmail, setShowFullEmail] = useState(false);
  const getDisplayEmail = (email: string) => {
    if (showFullEmail || email.length <= 24) return email;
    return email.slice(0, 24) + '...';
  };

  return (
    <div className="container max-w-2xl mx-auto py-16 px-4">
      {profile ? (
        <>
          <h1 className="text-3xl font-bold mb-2 flex items-center gap-2">
            <UserIcon className="h-7 w-7 text-primary" />
            User Profile
          </h1>
          {loading && <div className="py-8 text-center">Loading profile...</div>}
          {error && (
            <div className="flex items-center gap-2 p-4 mb-6 bg-red-100 border border-red-300 text-red-800 rounded-md">
              <AlertCircle className="h-5 w-5" />
              <span>{error}</span>
            </div>
          )}
          {profile && (
            <div>
              <Card>
                <CardHeader>
                  <div className="flex items-center gap-4">
                    {profile.avatar ? (
                      <img src={profile.avatar} alt="Avatar" className="h-16 w-16 rounded-full object-cover border" />
                    ) : (
                      <div className="h-16 w-16 rounded-full bg-muted flex items-center justify-center">
                        <UserIcon className="h-8 w-8 text-muted-foreground" />
                      </div>
                    )}
                    <div className="flex flex-col md:flex-row gap-6 w-full">
                      <div className="flex-1">
                        <CardTitle className="flex items-center gap-2">
                          {profile.username.length > 26 && !showFullUsername && (
                            <Button
                              type="button"
                              onClick={() => setShowFullUsername(true)}
                            >
                              {getDisplayUsername(profile.username)}
                            </Button>
                          ) || profile.username.length <= 26 && (
                            <span>{getDisplayUsername(profile.username)}</span>
                          )}
                          {profile.username.length > 26 && showFullUsername && (
                            <Button
                              type="button"
                              onClick={() => setShowFullUsername(false)}
                            >
                              {getDisplayUsername(profile.username)}
                            </Button>
                          )}
                        </CardTitle>
                        <CardDescription className="flex items-center gap-2">
                          <text className="text-center">Email:</text>
                          {profile.email.length > 24 && !showFullEmail && (
                            <Button
                              type="button"
                              size="sm"
                              className="mt-2 px-2 py-1 text-xs"
                              onClick={() => setShowFullEmail(true)}
                            >
                              {getDisplayEmail(profile.email)}
                            </Button>
                          )}
                          {profile.email.length > 24 && showFullEmail && (
                            <Button
                              type="button"
                              size="sm"
                              className="mt-2 px-2 py-1 text-xs"
                              onClick={() => setShowFullEmail(false)}
                            >
                              {getDisplayEmail(profile.email)}
                            </Button>
                          ) || profile.email.length <= 24 && (
                            <span>{getDisplayEmail(profile.email)}</span>
                          )}
                        </CardDescription>
                    {profile.company && <div className="text-sm text-muted-foreground">{profile.company}</div>}
                    <div className="flex gap-2 mt-1">
                      <Badge variant="secondary">{accountStatusLabel(profile.accountstatus)}</Badge>
                      <Badge variant="outline">{profile.membership}</Badge>
                      <Badge variant="default">{userStatusLabel(profile.status)}</Badge>
                    </div>
                    <div className="flex gap-2 mt-4">
                      <button
                        className="px-3 py-1 rounded bg-primary text-white font-semibold text-sm"
                        onClick={() => setEditOpen((v) => !v)}
                      >
                        {editOpen ? "Hide Editor" : "Edit Profile"}
                      </button>
                      {(isActive) ? (
                        <Button asChild variant="secondary" size="sm">
                          <Link to="/downloads" className="flex items-center">
                            <ExternalLink className="h-4 w-4 mr-2" />
                            Downloads
                          </Link>
                        </Button>
                      ) : (
                        <>{console.log("User does not have access to downloads: status = ", profile.accountstatus)}</>
                      )}
                    </div>
                  </div>
                  <div className="flex-1 flex flex-col gap-2 justify-center">
                    {profile.bio && (
                      <div>
                        <span className="font-semibold">Bio: </span>
                        <p className="text-muted-foreground text-sm mt-1">{profile.bio}</p>
                      </div>
                    )}
                    {profile.website && (
                      <div>
                        <span className="font-semibold">Website: </span>
                        <a href={profile.website} target="_blank" rel="noopener noreferrer" className="text-primary underline text-sm mt-1">{profile.website}</a>
                      </div>
                    )}
                  </div>
                </div>
                </div>
            </CardHeader>
            <CardContent>
                {editOpen && <ProfileEditForm profile={profile} onProfileUpdated={setProfile} />}
                {profile.restrictions && profile.restrictions.length > 0 && (
                <div className="mb-4">
                    <span className="font-semibold">Restrictions:</span>
                    <ul className="list-disc list-inside text-red-600 mt-1">
                    {profile.restrictions?.map((r, idx) => (
                        <li key={idx}>{r}</li>
                    ))}
                    </ul>
                </div>
                )}
            </CardContent>
            </Card>
        </div>
      )}
    </> 
    ) 
    : 
    (
      <div className="py-8 text-center">Loading profile . . .</div>
    )}
    </div>
  );
};


type ProfileEditFormProps = {
  profile: UserProfile;
  onProfileUpdated: (profile: UserProfile) => void;
};

const ProfileEditForm: React.FC<ProfileEditFormProps> = ({ profile, onProfileUpdated }) => {
  const [form, setForm] = useState<{
    bio: string;
    avatar: string;
    website: string;
    badges: number[];
  }>({
    bio: profile.bio || "",
    avatar: profile.avatar || "",
    website: profile.website || "",
    badges: profile.badges || [],
  });
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setForm((prev: typeof form) => ({ ...prev, [name]: value }));
  };

  {/*
  const handleBadgesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const badges = value.split(",").map((v) => parseInt(v.trim(), 10)).filter((n) => !isNaN(n));
    setForm((prev: typeof form) => ({ ...prev, badges }));
  };
  */}
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const updated = await profileAPI.updateProfile(form);
      onProfileUpdated(updated);
      setSuccess("Profile updated successfully.");
    } catch (err: any) {
      setError(err.message || "Failed to update profile.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <PageWithNavbar>
    <form onSubmit={handleSubmit} className="mb-6 space-y-4">
      <div>
        <label className="block font-semibold mb-1">Bio</label>
        <textarea
          name="bio"
          value={form.bio}
          onChange={handleChange}
          className="w-full border rounded p-2 min-h-[60px] text-primary-foreground"
        />
      </div>
      <div>
        <label className="block font-semibold mb-1">Avatar URL</label>
        <input
          type="text"
          name="avatar"
          value={form.avatar}
          onChange={handleChange}
          className="w-full border rounded p-2 text-primary-foreground"
        />
        {form.avatar && (
          <div className="mt-2 flex items-center gap-2">
            <img src={form.avatar} alt="Avatar Preview" className="h-16 w-16 rounded-full object-cover border" />
            <span className="text-xs text-muted-foreground">Preview</span>
          </div>
        )}
      </div>
      <div>
        <label className="block font-semibold mb-1">Website</label>
        <input
          type="text"
          name="website"
          value={form.website}
          onChange={handleChange}
          className="w-full border rounded p-2 text-primary-foreground"
        />
      </div>
      {/* 
      <div>
        <label className="block font-semibold mb-1 ">Badges (comma-separated IDs)</label>
        <input
          type="text"
          name="badges"
          value={form.badges?.join(", ")}
          onChange={handleBadgesChange}
          className="w-full border rounded p-2"
        />
        <div className="flex gap-2 mt-2 flex-wrap">
          {form.badges?.map((badgeId: number) => (
            <Badge key={badgeId} variant="default">#{badgeId}</Badge>
          ))}
        </div>
      </div>
      */}
      <button
        type="submit"
        className="px-4 py-2 rounded bg-primary text-white font-semibold disabled:opacity-60"
        disabled={saving}
      >
        {saving ? "Saving..." : "Save Changes"}
      </button>
      {success && <div className="text-green-600 mt-2">{success}</div>}
      {error && <div className="text-red-600 mt-2">{error}</div>}
    </form>
    </PageWithNavbar>
  );
};

// Account status enum mapping
function accountStatusLabel(status: number): string {
  const map = {
    0: "None",
    1: "Banned",
    2: "Pending Verification",
    3: "Inactive",
    4: "Suspended",
    20: "Active",
    50: "Moderator",
    100: "Administrator",
    200: "Super Administrator"
  } as const;
  if (status === undefined || status === null) return "Unknown";
  return map[status as keyof typeof map] || String(status);
}

// User status enum mapping
function userStatusLabel(status: number): string {
  const map = {
    0: "None",
    1: "Online",
    2: "Private",
    3: "Offline",
    100: "Banned",
    200: "Super Administrator",
  } as const;
  if (status === undefined || status === null) return "Unknown";
  return map[status as keyof typeof map] || String(status);
}

export default UserProfilePage;